import HiddenInput from "@/components/form/components/HiddenInput";
import { setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { Button, Flex, Form, Modal, Space } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateClientAssignedAgents } from "../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { AgentSelect } from "../../../enumerations/components/form/AgentSelect";
import { Client, UpdateClientAssignedAgents } from "../../types";

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  client: Client;
};

export const UpdateAssignedAgentsModalForm = ({ isOpen, onCancel, client }: Props) => {
  const { t } = useTranslation();

  const updateClientAssignedAgents = useUpdateClientAssignedAgents({ globalSpinner: false });

  const [form] = Form.useForm<UpdateClientAssignedAgents>();

  useEffect(() => {
    if (!isOpen) return;

    form.setFieldsValue({
      assignedAgentIds: client.assignedAgents?.map((agent) => agent.id) ?? [],
      optimisticLockVersion: client.optimisticLockVersion,
    });
  }, [client, isOpen]);

  const handleFormSubmit = () => {
    form.validateFields().then((values) => {
      updateClientAssignedAgents.mutate(
        { id: client.id, object: values },
        { onSuccess: onCancel, onError: (error) => setErrorsToForm(form, "client.attrs", error.violations) },
      );
    });
  };

  return (
    <Modal
      width={ModalSizes.SMALL}
      open={isOpen}
      title={t("client.titles.updateAssignedAgents")}
      afterClose={() => form.resetFields()}
      footer={null}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="updateAssignedAgentsModalForm">
        <HiddenInput name="optimisticLockVersion" />

        <Flex gap={rowGutter}>
          <Form.Item
            style={{ flex: 1 }}
            name="assignedAgentIds"
            label={t("client.attrs.assignedAgents")}
            rules={[validations.none]}
          >
            <AgentSelect allowClear isMultiple />
          </Form.Item>

          <Space className="form-item-without-label" align="start">
            <Button onClick={onCancel}>{t("common.cancel")}</Button>

            <Button
              type="primary"
              htmlType="submit"
              loading={updateClientAssignedAgents.isPending}
              onClick={handleFormSubmit}
            >
              {t("common.save")}
            </Button>
          </Space>
        </Flex>
      </Form>
    </Modal>
  );
};
